import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { DatePicker } from 'antd';
import styled, { createGlobalStyle, css } from 'styled-components';
import moment from 'moment';
import { EpochTimestampMs } from '../propTypes';
import RadioButtons from './RadioButtons';
import ControlWithLabel from './ControlWithLabel';
import { fromDaysToRangeBack } from '../utils/common';

const { RangePicker } = DatePicker;

export const PRESETS_DAYS = [7, 30, 90, 180, 360];

const DateRangeWithPresets = ({
  from,
  to,
  onChange,
  className,
  isDisableFutureDates
}) => {
  const presetValue = useMemo(
    () => fromRangeToPresetDays(from, to),
    [from, to]
  );

  const onPresetChange = (daysDiff) => {
    const { from, to } = fromDaysToRangeBack(daysDiff);

    onChange(from, to);
  };

  const onCustomRangeChange = ([customFrom, customTo]) => {
    const nowMoment = moment();
    const fromMoment = moment(customFrom);
    const toMoment = moment(customTo);

    const finalFrom =
      nowMoment.diff(fromMoment) < 0
        ? nowMoment.clone().subtract(1, 'days')
        : fromMoment;
    const finalTo = nowMoment.diff(toMoment) < 0 ? nowMoment : toMoment;

    onChange(
      finalFrom.startOf('day').valueOf(),
      finalTo.endOf('day').valueOf()
    );
  };

  const disabledFutureDate = (current) => {
    // Disable dates after the new "to" date
    return isDisableFutureDates ? current && current > moment() : null;
  };

  return (
    <Container className={`DateRangeWithPresets-root ${className}`}>
      <StyledRadioButtons
        label="Duration"
        options={RADIO_OPTIONS}
        value={presetValue}
        onChange={onPresetChange}
        className="DateRangeWithPresets-duration"
      />
      <ControlWithLabel label="Date Range">
        <StyledRangePicker
          value={[moment(from), moment(to)]}
          onChange={onCustomRangeChange}
          size="small"
          separator="~"
          format="DD/MM/YYYY"
          allowClear={false}
          disabledDate={disabledFutureDate}
          dropdownClassName="DateRangeWithPresets-popup"
        />
      </ControlWithLabel>
      <GlobalStyle />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const brandBackgroundStyle = css`
  background: #21b4bd;
`;

const StyledRadioButtons = styled(RadioButtons)`
  margin-right: 22px;
`;

const StyledRangePicker = styled(RangePicker)`
  width: 232px;

  &.ant-picker-focused,
  &.ant-picker:hover {
    border-color: #21b4bd;
  }

  .ant-picker-input {
    width: 85px;
  }

  &.ant-picker-range .ant-picker-active-bar {
    ${brandBackgroundStyle}
  }
`;

const GlobalStyle = createGlobalStyle`
  .DateRangeWithPresets-popup {
    .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-cell-inner,
    .ant-picker-cell-in-view.ant-picker-cell-range-start .ant-picker-cell-inner,
    .ant-picker-cell-in-view.ant-picker-cell-range-end .ant-picker-cell-inner {
      ${brandBackgroundStyle}
    }

    .ant-picker-cell-in-view.ant-picker-cell-today
    .ant-picker-cell-inner::before {
      border-color: #21b4bd;
    }
  }
`;

const RADIO_OPTIONS = PRESETS_DAYS.map((number) => ({
  value: number,
  label: `${number} Days`
}));

export const fromRangeToPresetDays = (from, to) => {
  // if range is not specified or its end date is not today
  if (!from || !to || moment(to).diff(moment(), 'days') !== 0) {
    return null;
  }

  // calculate number of days between start and end timestamps
  const daysDiff = Math.round(moment(to).diff(moment(from), 'days', true));

  return PRESETS_DAYS.includes(daysDiff) ? daysDiff : null;
};

DateRangeWithPresets.propTypes = {
  from: EpochTimestampMs,
  to: EpochTimestampMs,
  onChange: PropTypes.func.isRequired, // (from, to) => {}
  className: PropTypes.string
};

export default DateRangeWithPresets;
